// src/UserContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { getUser } from "./graphql/queries";
import { createUser, updateUser } from "./graphql/mutations";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

interface UserContextType {
  user: any;
  loading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchUserData = async () => {
    try {
      const { userId, signInDetails } = await getCurrentUser();
      const loginId = signInDetails?.loginId ?? userId;
      const payload = (signInDetails as any)?.payload ?? {};
      const nameAttr = payload.name ?? loginId;
      const emailAttr = payload.email ?? "";

      const { data } = await client.graphql({
        query: getUser,
        variables: { id: userId },
      });

      let userData = data?.getUser;

      if (!userData) {
        const { data: createData } = await client.graphql({
          query: createUser,
          variables: {
            input: {
              id: userId,
              name: nameAttr,
              username: loginId,
              email: emailAttr,
            },
          },
        });
        userData = createData?.createUser;
      }

      if (
        userData &&
        (!userData.name ||
          userData.name === userData.id ||
          !userData.username ||
          userData.username === userData.id)
      ) {
        const updated = await client.graphql({
          query: updateUser,
          variables: {
            input: {
              id: userData.id,
              name: userData.owner, // fallback to Cognito username
              username: userData.owner,
            },
          },
        });
        userData = updated.data.updateUser;
      }

      if (userData) {
        setUser({
          id: userData.id,
          name: userData.name,
          email: userData.email,
          username: userData.username,
          owner: userData.owner,
          avatarUrl: userData.avatarUrl,
          hobbies: userData.hobbies,
          favoriteColors: userData.favoriteColors,
        });
      }
    } catch (error) {
      console.error("Error fetching or creating user:", error);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchUserData();
      setLoading(false);
    };
    load();
  }, []);

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <UserContext.Provider
      value={{ user, loading, logout, refreshUser: fetchUserData }}
    >
      {children}
    </UserContext.Provider>
  );
};
